import React, { useState, useEffect } from 'react';

interface ComplianceMetric {
  id: string;
  name: string;
  score: number;
  maxScore: number;
  category: 'quality' | 'transparency' | 'customer_service' | 'lab_testing';
  lastUpdated: string;
  details: string;
}

interface AccountabilityData {
  vendorId: string;
  vendorName: string;
  overallScore: number;
  transparencyScore: number;
  qualityScore: number;
  customerServiceScore: number;
  labTestingScore: number;
  metrics: ComplianceMetric[];
  alerts: AccountabilityAlert[];
  reviewResponseRate: number;
  averageResponseTime: number; // hours
  labResultsUpToDate: boolean;
  disputeCount: number;
  lastAudit: string;
}

interface AccountabilityAlert {
  id: string;
  type: 'warning' | 'violation' | 'improvement_needed';
  severity: 'low' | 'medium' | 'high' | 'critical';
  message: string;
  category: string;
  createdAt: string;
  resolved: boolean;
  dueDate?: string;
}

interface VendorAccountabilityProps {
  vendorId?: string;
  isStaffView?: boolean;
}

export default function VendorAccountability({ vendorId, isStaffView = false }: VendorAccountabilityProps) {
  const [accountabilityData, setAccountabilityData] = useState<AccountabilityData | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  // Mock data - replace with actual API calls
  useEffect(() => {
    const fetchAccountabilityData = async () => {
      setLoading(true);
      
      // Simulate API call
      setTimeout(() => {
        const mockData: AccountabilityData = {
          vendorId: vendorId || '1',
          vendorName: 'Premium Kratom Co',
          overallScore: 87,
          transparencyScore: 92,
          qualityScore: 85,
          customerServiceScore: 88,
          labTestingScore: 84,
          reviewResponseRate: 94,
          averageResponseTime: 4.2,
          labResultsUpToDate: true,
          disputeCount: 2,
          lastAudit: '2024-01-15',
          metrics: [
            {
              id: '1',
              name: 'Lab Results Current',
              score: 10,
              maxScore: 10,
              category: 'lab_testing',
              lastUpdated: '2024-01-20',
              details: 'All products have current lab results within 6 months'
            },
            {
              id: '2',
              name: 'Product Information Accuracy',
              score: 18,
              maxScore: 20,
              category: 'transparency',
              lastUpdated: '2024-01-18',
              details: 'Minor inconsistencies in alkaloid percentages on 2 products'
            },
            {
              id: '3',
              name: 'Customer Review Response',
              score: 17,
              maxScore: 20,
              category: 'customer_service',
              lastUpdated: '2024-01-19',
              details: 'Responds to 94% of reviews within 48 hours'
            },
            {
              id: '4',
              name: 'Quality Control Standards',
              score: 16,
              maxScore: 20,
              category: 'quality',
              lastUpdated: '2024-01-17',
              details: 'Follows most quality standards, some packaging improvements needed'
            }
          ],
          alerts: [
            {
              id: '1',
              type: 'improvement_needed',
              severity: 'medium',
              message: 'Lab results for Red Maeng Da batch #KR-2024-001 expire in 2 weeks',
              category: 'Lab Testing',
              createdAt: '2024-01-20',
              resolved: false,
              dueDate: '2024-02-05'
            },
            {
              id: '2',
              type: 'warning',
              severity: 'low',
              message: 'Customer service response time increased to 6 hours average',
              category: 'Customer Service',
              createdAt: '2024-01-18',
              resolved: false
            }
          ]
        };
        
        setAccountabilityData(mockData);
        setLoading(false);
      }, 1000);
    };

    fetchAccountabilityData();
  }, [vendorId]);

  const getScoreColor = (score: number, maxScore: number) => {
    const percentage = (score / maxScore) * 100;
    if (percentage >= 90) return '#10b981'; // green
    if (percentage >= 80) return '#f59e0b'; // yellow
    if (percentage >= 70) return '#f97316'; // orange
    return '#ef4444'; // red
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return '#dc2626';
      case 'high': return '#ea580c';
      case 'medium': return '#d97706';
      case 'low': return '#65a30d';
      default: return '#6b7280';
    }
  };

  const filteredMetrics = selectedCategory === 'all' 
    ? accountabilityData?.metrics || []
    : accountabilityData?.metrics.filter(m => m.category === selectedCategory) || [];

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ display: 'inline-block', width: '32px', height: '32px', border: '3px solid #f3f3f3', borderTop: '3px solid #3498db', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
        <p>Loading accountability data...</p>
      </div>
    );
  }

  if (!accountabilityData) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>No accountability data available</div>;
  }

  return (
    <div style={{ padding: '1.5rem', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '1.875rem', fontWeight: 'bold', marginBottom: '0.5rem', color: '#e2e8f0' }}>
          {isStaffView ? 'Vendor Accountability Dashboard' : 'Accountability Report'}
        </h2>
        <p style={{ color: '#6b7280', fontSize: '1rem' }}>
          {accountabilityData.vendorName} - Transparency and Quality Metrics
        </p>
      </div>

      {/* Overall Score Summary */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
        gap: '1rem', 
        marginBottom: '2rem' 
      }}>
        <div style={{ 
          backgroundColor: '#2d3748', 
          padding: '1.5rem', 
          borderRadius: '0.5rem', 
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <h3 style={{ fontSize: '2.5rem', fontWeight: 'bold', color: getScoreColor(accountabilityData.overallScore, 100), margin: '0' }}>
            {accountabilityData.overallScore}
          </h3>
          <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: '0.25rem 0 0 0' }}>Overall Score</p>
        </div>
        
        <div style={{ 
          backgroundColor: '#2d3748', 
          padding: '1.5rem', 
          borderRadius: '0.5rem', 
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <h3 style={{ fontSize: '2rem', fontWeight: 'bold', color: getScoreColor(accountabilityData.transparencyScore, 100), margin: '0' }}>
            {accountabilityData.transparencyScore}
          </h3>
          <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: '0.25rem 0 0 0' }}>Transparency</p>
        </div>
        
        <div style={{ 
          backgroundColor: '#2d3748', 
          padding: '1.5rem', 
          borderRadius: '0.5rem', 
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <h3 style={{ fontSize: '2rem', fontWeight: 'bold', color: getScoreColor(accountabilityData.qualityScore, 100), margin: '0' }}>
            {accountabilityData.qualityScore}
          </h3>
          <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: '0.25rem 0 0 0' }}>Quality</p>
        </div>
        
        <div style={{ 
          backgroundColor: '#2d3748', 
          padding: '1.5rem', 
          borderRadius: '0.5rem', 
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <h3 style={{ fontSize: '2rem', fontWeight: 'bold', color: getScoreColor(accountabilityData.customerServiceScore, 100), margin: '0' }}>
            {accountabilityData.customerServiceScore}
          </h3>
          <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: '0.25rem 0 0 0' }}>Customer Service</p>
        </div>
      </div>

      {/* Key Metrics */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
        gap: '1rem', 
        marginBottom: '2rem' 
      }}>
        <div style={{ backgroundColor: '#2d3748', padding: '1.5rem', borderRadius: '0.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h4 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1rem', color: '#e2e8f0' }}>Response Metrics</h4>
          <div style={{ marginBottom: '0.75rem' }}>
            <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>Review Response Rate</span>
            <div style={{ fontWeight: 'bold', fontSize: '1.25rem', color: '#059669' }}>{accountabilityData.reviewResponseRate}%</div>
          </div>
          <div>
            <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>Avg Response Time</span>
            <div style={{ fontWeight: 'bold', fontSize: '1.25rem', color: '#0891b2' }}>{accountabilityData.averageResponseTime}h</div>
          </div>
        </div>
        
        <div style={{ backgroundColor: '#2d3748', padding: '1.5rem', borderRadius: '0.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h4 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1rem', color: '#e2e8f0' }}>Compliance Status</h4>
          <div style={{ marginBottom: '0.75rem' }}>
            <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>Lab Results Current</span>
            <div style={{ fontWeight: 'bold', fontSize: '1.25rem', color: accountabilityData.labResultsUpToDate ? '#059669' : '#dc2626' }}>
              {accountabilityData.labResultsUpToDate ? 'Yes' : 'No'}
            </div>
          </div>
          <div>
            <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>Active Disputes</span>
            <div style={{ fontWeight: 'bold', fontSize: '1.25rem', color: accountabilityData.disputeCount > 0 ? '#dc2626' : '#059669' }}>
              {accountabilityData.disputeCount}
            </div>
          </div>
        </div>
      </div>

      {/* Active Alerts */}
      {accountabilityData.alerts.filter(alert => !alert.resolved).length > 0 && (
        <div style={{ backgroundColor: '#2d3748', padding: '1.5rem', borderRadius: '0.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '2rem' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', color: '#e2e8f0' }}>Active Alerts</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {accountabilityData.alerts.filter(alert => !alert.resolved).map(alert => (
              <div key={alert.id} style={{ 
                padding: '1rem', 
                borderRadius: '0.375rem', 
                backgroundColor: '#fef7f7', 
                borderLeft: `4px solid ${getSeverityColor(alert.severity)}` 
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                  <span style={{ 
                    fontSize: '0.75rem', 
                    fontWeight: '600', 
                    textTransform: 'uppercase', 
                    color: getSeverityColor(alert.severity) 
                  }}>
                    {alert.severity} {alert.type.replace('_', ' ')}
                  </span>
                  <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>{alert.category}</span>
                </div>
                <p style={{ color: '#a0aec0', margin: '0', fontSize: '0.875rem' }}>{alert.message}</p>
                {alert.dueDate && (
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.5rem 0 0 0' }}>
                    Due: {new Date(alert.dueDate).toLocaleDateString()}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Detailed Metrics */}
      <div style={{ backgroundColor: '#2d3748', padding: '1.5rem', borderRadius: '0.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#e2e8f0', margin: '0' }}>Detailed Metrics</h3>
          <select 
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            style={{ 
              padding: '0.5rem', 
              borderRadius: '0.375rem', 
              border: '1px solid #4a5568',
              backgroundColor: '#2d3748',
              fontSize: '0.875rem'
            }}
          >
            <option value="all">All Categories</option>
            <option value="quality">Quality</option>
            <option value="transparency">Transparency</option>
            <option value="customer_service">Customer Service</option>
            <option value="lab_testing">Lab Testing</option>
          </select>
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {filteredMetrics.map(metric => (
            <div key={metric.id} style={{ 
              padding: '1rem', 
              border: '1px solid #4a5568', 
              borderRadius: '0.375rem',
              backgroundColor: '#fafafa'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#e2e8f0', margin: '0' }}>{metric.name}</h4>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontSize: '1.125rem', fontWeight: 'bold', color: getScoreColor(metric.score, metric.maxScore) }}>
                    {metric.score}/{metric.maxScore}
                  </span>
                  <div style={{ 
                    width: '100px', 
                    height: '8px', 
                    backgroundColor: '#e5e7eb', 
                    borderRadius: '4px',
                    overflow: 'hidden'
                  }}>
                    <div style={{ 
                      width: `${(metric.score / metric.maxScore) * 100}%`, 
                      height: '100%', 
                      backgroundColor: getScoreColor(metric.score, metric.maxScore),
                      transition: 'width 0.3s ease'
                    }}></div>
                  </div>
                </div>
              </div>
              <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: '0 0 0.5rem 0' }}>{metric.details}</p>
              <p style={{ fontSize: '0.75rem', color: '#9ca3af', margin: '0' }}>
                Last updated: {new Date(metric.lastUpdated).toLocaleDateString()}
              </p>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}